"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import { ArrowLeft } from "lucide-react";

export function DisclaimerClient() {
  const siteName = process.env.NEXT_PUBLIC_SITE_NAME ?? "Meazora";
  const [activeSection, setActiveSection] = useState("advice");

  useEffect(() => {
    const sections = document.querySelectorAll("section[id]");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { rootMargin: "-20% 0px -60% 0px" }
    );

    sections.forEach((section) => observer.observe(section));
    return () => {
      sections.forEach((section) => observer.unobserve(section));
    };
  }, []);

  const menuItems = [
    { id: "advice", label: "1. Not Engineering Advice" },
    { id: "warranty", label: "2. No Warranties & Specs" },
    { id: "projections", label: "3. Cost & Material Volatility" },
    { id: "codes", label: "4. Building Codes & Permitting" },
    { id: "liability", label: "5. Limitation of Liability" },
    { id: "references", label: "6. External Standards" },
  ];

  return (
    <div className="max-w-6xl mx-auto py-10 px-4 md:px-6">
      {/* Back Link */}
      <div className="mb-10 animate-in fade-in slide-in-from-top-4 duration-300">
        <Link
          href="/"
          className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-500 hover:text-slate-800 dark:text-zinc-400 dark:hover:text-zinc-200 transition-colors group font-sans"
        >
          <ArrowLeft size={14} className="group-hover:-translate-x-0.5 transition-transform" />
          Back to Calculators
        </Link>
      </div>

      {/* Header */}
      <div className="border-b border-slate-200 dark:border-zinc-800 pb-8 mb-12 animate-in fade-in duration-500">
        <span className="text-[10px] font-mono font-bold uppercase tracking-widest text-sky-600 dark:text-sky-400">
          Important Disclosure
        </span>
        <h1 className="text-3xl md:text-5xl font-black text-slate-900 dark:text-slate-55 mt-2 mb-4 tracking-tight leading-none font-sans">
          Disclaimer — Meazora
        </h1>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-500 dark:text-zinc-500 font-mono">
          <span>Last updated: July 12, 2026</span>
          <span className="w-1.5 h-1.5 rounded-full bg-slate-300 dark:bg-zinc-750" />
          <span>Read time: 2 min</span>
        </div>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-12 items-start">
        {/* Sticky Sidebar */}
        <aside className="lg:col-span-1 sticky top-24 hidden lg:block select-none">
          <nav className="flex flex-col gap-3 font-sans text-xs">
            <span className="text-[9px] font-bold text-slate-400 dark:text-zinc-500 uppercase tracking-widest pl-3 mb-1">
              Sections
            </span>
            {menuItems.map((item) => (
              <a
                key={item.id}
                href={`#${item.id}`}
                className={`py-1.5 pl-3 border-l-2 text-left font-medium transition-all duration-200 ${
                  activeSection === item.id
                    ? "border-sky-600 dark:border-sky-450 text-sky-600 dark:text-sky-400 font-semibold"
                    : "border-transparent text-slate-400 dark:text-zinc-500 hover:text-slate-700 dark:hover:text-zinc-300"
                }`}
              >
                {item.label}
              </a>
            ))}
          </nav>
        </aside>

        {/* Content Area */}
        <div className="lg:col-span-3 space-y-16 font-sans text-slate-650 dark:text-zinc-300">
          <section id="advice" className="scroll-mt-28 space-y-4">
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50 tracking-tight font-sans">
              1. Not Certified Engineering or Building Advice
            </h2>
            <p className="text-sm leading-relaxed">
              The calculations, formulas, material estimators, and project cost projections generated by {siteName} are provided strictly for <strong className="font-semibold text-slate-900 dark:text-slate-100">educational, modeling, and illustrative planning purposes</strong>.
            </p>
            <p className="text-sm leading-relaxed">
              They do not constitute professional architectural blueprints, certified structural engineering specifications, or formal contractor bids. Utilizing these tools does not establish a professional client relationship or contracting agreement. We strongly recommend consulting with a licensed general contractor, structural engineer, or specialized tradesperson before purchasing major building materials or commencing demolition/construction.
            </p>
          </section>

          <section id="warranty" className="scroll-mt-28 space-y-4">
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50 tracking-tight font-sans">
              2. No Warranties &amp; Estimation Margins
            </h2>
            <p className="text-sm leading-relaxed">
              All {siteName} calculators, guides, site prep checklists, and FAQs are provided on an <strong className="font-semibold text-slate-900 dark:text-slate-100">&ldquo;AS IS&rdquo;</strong> basis, without warranties of any kind, express or implied. To the maximum extent permitted by law, {siteName} disclaims all warranties, including but not limited to implied warranties of merchantability, fitness for a particular purpose, and non-infringement.
            </p>
            <p className="text-sm leading-relaxed">
              We do not guarantee the accuracy, completeness, or applicability of any calculator result, coverage estimate, or waste percentage. While we base our metrics on official construction standards (like ACI concrete benchmarks, TCNA tiling metrics, and GA drywall guidelines), actual jobsite variables (such as wall irregularities, concrete spillage, subfloor warping, and lumber grading flaws) will cause real-world material needs to vary. Always check with product manufacturers and physical measurements before ordering materials.
            </p>
          </section>

          <section id="projections" className="scroll-mt-28 space-y-4">
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50 tracking-tight font-sans">
              3. Cost &amp; Material Volatility
            </h2>
            <p className="text-sm leading-relaxed">
              Estimators representing project labor costs or material retail pricing are hypothetical modeling systems. They use broad averages and historical indexes.
            </p>
            <div className="border-l-2 border-sky-500/50 pl-4 py-1 my-4 bg-slate-50/50 dark:bg-zinc-900/30 rounded-r-lg text-xs text-slate-500 dark:text-zinc-400">
              Inherent fluctuations in lumber pricing, local concrete delivery surcharges, regional labor rate variations, and supply chain updates mean actual local contractor quotes will diverge from the estimates shown on this site. Past average project costs do not guarantee final builder invoice matching.
            </div>
          </section>

          <section id="codes" className="scroll-mt-28 space-y-4">
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50 tracking-tight font-sans">
              4. Local Building Codes &amp; Permitting Compliance
            </h2>
            <p className="text-sm leading-relaxed">
              {siteName} does not verify or guarantee compliance with your specific municipal zoning regulations, state building codes, or Homeowners Association (HOA) rules. 
            </p>
            <p className="text-sm leading-relaxed">
              Standards such as fence setback lines, structural post hole depths, and deck spans are subject to local code modifications (e.g. frost line variations, high-wind zones, or seismic reinforcement needs). Always check with your local permitting office and call the national utility digging safety line (811) before performing any excavation or construction.
            </p>
          </section>

          <section id="liability" className="scroll-mt-28 space-y-4">
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50 tracking-tight font-sans">
              5. Limitation of Liability
            </h2>
            <p className="text-sm leading-relaxed">
              Under no circumstances shall {siteName}, its creators, contributors, or affiliates be liable for any direct, indirect, incidental, special, consequential, or punitive damages — including but not limited to financial loss, project delays, material shortages, over-ordering costs, structural failure, property damage, personal injury, or municipal code violations — arising from your use of or reliance on any calculator output, guide, FAQ, or related project connection on this website, even if advised of the possibility of such damages.
            </p>
            <p className="text-sm leading-relaxed">
              Your use of the Service is at your sole risk. You assume full responsibility for verifying the accuracy of any calculations against real-world physical measurements and engineering plans.
            </p>
          </section>

          <section id="references" className="scroll-mt-28 space-y-4">
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-50 tracking-tight font-sans">
              6. External Standards &amp; Manufacturers
            </h2>
            <p className="text-sm leading-relaxed">
              Links or references to industry standards (e.g. ACI standards, TCNA manuals, Gypsum Association guides) or manufacturer tools (e.g. Sherwin-Williams paint formulas) are provided for auditing and educational verification convenience. While we attempt to keep these citations current, we make no warranties regarding their completeness or continuous accuracy.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
